import {useAuth0} from "@auth0/auth0-react";
import {Dayjs} from "dayjs";
import {handleApiError} from "@/graphql/api.tsx";

const endpointUrl = import.meta.env.VITE_APP_BACKEND_URL + "/graphql";
export type FieldValueType =
  | string
  | number
  | boolean
  | undefined
  | Dayjs
  | string[]
  | null
  | {
  index: number,
  address: string,
  chiban: string,
  chimoku: string,
  chiseki: string,
  rightType: string,
  rightsRatio: string,
}[]
  | { tenant_age: string; tenant_name: string; }[]
  ;

export class ApiError extends Error {
}

export class InternalServerError extends ApiError {
}

export enum GraphQLErrorCode {
  INTERNAL_SERVER_ERROR = "INTERNAL_SERVER_ERROR",
  NOT_FOUND = "NOT_FOUND",
  UNAUTHORIZED = "UNAUTHORIZED",
  FORBIDDEN = "FORBIDDEN",
  USAGE_LIMIT_EXCEEDED = "USAGE_LIMIT_EXCEEDED",
}

export class GraphqlError extends ApiError {
  public code: GraphQLErrorCode;
  
  constructor(
    message: string,
    code: GraphQLErrorCode = GraphQLErrorCode.INTERNAL_SERVER_ERROR,
  ) {
    super(message);
    this.code = code;
  }
}

export function useFetcher<TData, TVariables>(
  query: string,
  options?: RequestInit["headers"],
) {
  const {getAccessTokenSilently} = useAuth0();
  
  return async (variables?: TVariables): Promise<TData> => {
    const res = await fetch(endpointUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await getAccessTokenSilently({
          authorizationParams: {
            audience: "ai-zyusetu"
          }
        })}`,
        ...options,
      },
      body: JSON.stringify({
        query,
        variables,
      }),
    });
    
    if (500 <= res.status && res.status < 600) {
      throw new InternalServerError();
    }
    
    const json = await res.json();
    
    if (json.errors) {
      const {
        message,
        extensions: {code},
      } = json.errors[0] || {};
      await handleApiError(code, message);
      throw new GraphqlError(message || "Internal Server Error", code);
    }
    
    return json.data;
  };
}

export async function fetchSync<TData, TVariables>(
  getAccessTokenSilently: (params: any) => Promise<string>,
  query: string,
  variables?: TVariables,
  options?: RequestInit["headers"],
): Promise<TData> {
  const res = await fetch(endpointUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getAccessTokenSilently({
        authorizationParams: {
          audience: "ai-zyusetu"
        }
      })}`,
      ...options,
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  });
  
  if (500 <= res.status && res.status < 600) {
    throw new InternalServerError();
  }
  
  const json = await res.json();
  if (json.errors) {
    const {
      message,
      extensions: {code},
    } = json.errors[0] || {};
    throw new GraphqlError(message || "Internal Server Error", code);
  }
  
  return json.data;
}